import { displayDALocation } from './daDisplay';
import { canUseInternalAdmin } from './internalUsers';
import { getPipelineCost } from './pipelineCost';
import { shortCouncilName } from '../config/registry';

const ROLE_FIELDS = [
  ['builder', 'Builder', 'builder_name', 'builder_company'],
  ['architect', 'Architect', 'architect_name', 'architect_company'],
  ['applicant', 'Applicant', 'applicant_name', 'applicant_company'],
  ['owner', 'Owner', 'owner_name', null],
];

function normaliseEmail(email) {
  return String(email || '').trim().toLowerCase();
}

export function canUseOutreachPacks(email) {
  return canUseInternalAdmin(normaliseEmail(email));
}

function formatMoney(value) {
  const num = Number(value || 0);
  if (!Number.isFinite(num) || num <= 0) return null;
  if (num >= 1000000) return `$${(num / 1000000).toFixed(num >= 10000000 ? 0 : 1)}M`;
  if (num >= 1000) return `$${Math.round(num / 1000)}K`;
  return `$${Math.round(num)}`;
}

function shortCouncil(council) {
  return shortCouncilName(council);
}

function countBy(rows, getter) {
  const counts = new Map();
  for (const row of rows) {
    const key = getter(row) || 'Unknown';
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
}

function roleHits(rows) {
  return ROLE_FIELDS.map(([, label, nameField, companyField]) => {
    const values = new Set();
    for (const row of rows) {
      const value = [row[nameField], companyField ? row[companyField] : null].filter(Boolean).join(' / ');
      if (value) values.add(value);
    }
    return { label, count: values.size, examples: [...values].slice(0, 5) };
  }).filter(role => role.count > 0);
}

function daLine(da) {
  const cost = formatMoney(getPipelineCost(da));
  const bits = [
    da.da_number || da.council_application_number || da.planning_portal_app_number || 'No DA number',
    displayDALocation(da, 'Street address not provided'),
    shortCouncil(da.council),
    da.status || 'Unknown status',
    cost,
  ].filter(Boolean);
  return `- ${bits.join(' | ')}`;
}

export function generateOutreachPack({
  search,
  rows,
  councils = [],
  generatedFor,
  maxRows = 50,
}) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const topRows = safeRows.slice(0, maxRows);
  const councilCounts = countBy(safeRows, row => shortCouncil(row.council));
  const statusCounts = countBy(safeRows, row => row.status || 'Unknown');
  const roles = roleHits(safeRows);
  const totalValue = safeRows.reduce((sum, row) => sum + Number(getPipelineCost(row) || 0), 0);
  const generatedAt = new Date().toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });

  return [
    `# SiteScope Outreach Pack: ${search || 'Current DA Search'}`,
    '',
    `Generated for: ${generatedFor || 'internal SiteScope user'}`,
    `Generated at: ${generatedAt}`,
    `Coverage filter: ${Array.isArray(councils) && councils.length ? councils.map(shortCouncil).join(', ') : 'All councils'}`,
    `Source: SiteScope app search/export. Do not treat this as a live database connector.`,
    '',
    '## DA Footprint',
    '',
    `Total matching DAs: ${safeRows.length}`,
    `Visible pipeline value: ${formatMoney(totalValue) || 'No cost data'}`,
    '',
    '### Councils',
    '',
    ...(councilCounts.length ? councilCounts.map(([label, count]) => `- ${label}: ${count}`) : ['- None']),
    '',
    '### Status Mix',
    '',
    ...(statusCounts.length ? statusCounts.map(([label, count]) => `- ${label}: ${count}`) : ['- None']),
    '',
    '## Roles Found In Matching DAs',
    '',
    ...(roles.length
      ? roles.flatMap(role => [
          `- ${role.label}: ${role.count} unique visible value${role.count === 1 ? '' : 's'}`,
          ...role.examples.map(example => `  - ${example}`),
        ])
      : ['- No visible role fields in this result set.']),
    '',
    '## Recent / Relevant DAs',
    '',
    ...(topRows.length ? topRows.map(daLine) : ['- No DAs in current result set.']),
    safeRows.length > topRows.length ? `- Plus ${safeRows.length - topRows.length} more matching DAs not included in this pack.` : '',
    '',
    '## Claude Prompt',
    '',
    'Use this SiteScope outreach pack to draft a concise, practical customer reach-out sequence. Focus on what the business is doing in the Northern Rivers, where their activity is strongest, what opportunities SiteScope can surface earlier, and why a 15-minute walkthrough is worth their time. Do not invent contacts, DA facts, or claims not supported by this pack.',
  ].filter(line => line !== '').join('\n');
}
